import { useCallback, useEffect, useRef, useState } from "react";

import type { Cluster } from "./ClusterCard.js";
import { Icon } from "./Icon.js";
import { Toggle as Switch } from "./Toggle.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

/**
 * La scheda IMPOSTAZIONI CLUSTER, portata da `SettingsClustersTab.qml`.
 *
 * E' la scheda piu' densa del programma, e la densita' e' governata da una
 * regola sola: **si mostra solo quello che quel rig ha davvero**. Il modello
 * PTZ e' il selettore principale e decide il resto — un campo "indirizzo
 * NPort" su un rig IP-nativo e' un invito a compilarlo, e chi lo compila
 * rompe la ronda.
 *
 * Le sezioni sono quelle del `.qml`, nello stesso ordine: anagrafica, DCS,
 * modello PTZ, camera (piu' la seconda ottica IR), trasporto PTZ, trasporto
 * sensore, anemometro, puntatore laser, marker sul video, limiti, velocita'
 * di ronda, banda morta e ritardi, planimetria, fuori servizio, tracce.
 */

type Val = string | number | boolean | null | undefined;
type Form = Record<string, Val>;

/** Lo stato del cluster come classe CSS. Sconosciuto resta `muted`: nel .qml
 *  `_statusColor` fa lo stesso, e un colore inventato direbbe una cosa che
 *  nessuno ha misurato. */
function statusKey(status: string | null | undefined): string {
  const s = (status ?? "").toUpperCase();
  return s === "CRITICAL" || s === "WARNING" || s === "NORMAL" ? s.toLowerCase() : "muted";
}

/**
 * L'estremo a cui parla davvero il PTZ, con le tre forme del `.qml`:
 * ONVIF passa dall'IP della camera, Pelco-D via NPort da `ptzIp:ptzPort`, e
 * il resto e' seriale diretta.
 */
function ptzEndpoint(c: Cluster): string {
  if (c.cameraProtocol === "onvif") return `${c.cameraIp || "—"} (ONVIF)`;
  if (c.ptzTransportMode === "nport") return `${c.ptzIp || "?"}:${c.ptzPort || "?"}`;
  return "DIRECT";
}

/** Acceso solo quando il comando viaggia in rete: sulla seriale non c'e'
 *  nessun indirizzo da controllare, e accenderlo lo stesso spegnerebbe la
 *  differenza. */
function ptzAccent(c: Cluster): boolean {
  return c.cameraProtocol === "onvif" || c.ptzTransportMode === "nport";
}

const PTZ_MODELS = ["indusvision", "tecnovideo", "bik-inform", "custom"];
const CAMERA_PROTOCOLS = ["pelco-d", "dahua-cgi", "onvif"];
const SENSOR_MODES = ["direct", "hesai", "allred"];

/**
 * Il modello sceglie i campi tecnici sottostanti.
 *
 * Sono le stesse assegnazioni del `.qml`, e ognuna ha una ragione misurata sul
 * campo: il rig BIK-INFORM e' IP-nativo (quindi `direct`, non `nport`), parla
 * sulla porta 61556 che ha di fabbrica, ed e' cosi' lento che con la velocita'
 * di ronda predefinita la testa sembrerebbe ferma.
 */
function presetFor(model: string, form: Form): Form {
  if (model === "indusvision") {
    return { ptzModel: model, cameraProtocol: "onvif", ptzTransportMode: "direct" };
  }
  if (model === "tecnovideo") {
    return { ptzModel: model, cameraProtocol: "pelco-d", ptzTransportMode: "nport" };
  }
  if (model === "bik-inform") {
    const patch: Form = {
      ptzModel: model,
      cameraProtocol: "pelco-d",
      ptzTransportMode: "direct",
    };
    const port = Number(form["ptzPort"]);
    if (!port || port === 4001) patch["ptzPort"] = 61556;
    const speed = Number(form["ptzAutoSpeed"]);
    if (!speed || speed <= 10) patch["ptzAutoSpeed"] = 50;
    return patch;
  }
  return { ptzModel: model };
}

export function ClustersTab({ onCalibrate }: { onCalibrate?: ((c: Cluster) => void) | undefined }) {
  const { t } = useI18n();
  const { api } = useSession();

  const [rows, setRows] = useState<Cluster[]>([]);
  const [kpis, setKpis] = useState<{ totalAlarms?: number; unacknowledged?: number }>({});
  const [editing, setEditing] = useState("");
  const [form, setForm] = useState<Form>({});
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<Form>({ thresholdL1: 100, thresholdL2: 500 });
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);

  const fail = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.isForbidden) setDenied(true);
    else setError(e instanceof ApiError ? e.message : String(e));
  }, []);

  const reload = useCallback(async () => {
    try {
      setRows(await api.call<Cluster[]>("clusters.list"));
      setDenied(false);
      setError("");
    } catch (e) {
      fail(e);
    }
    try {
      setKpis(await api.call("alarms.kpis"));
    } catch {
      /* i contatori sono un di piu': la scheda funziona anche senza */
    }
  }, [api, fail]);

  useEffect(() => void reload(), [reload]);

  const setF = (k: string, v: Val) => setForm((f) => ({ ...f, [k]: v }));

  const open = async (id: string) => {
    if (editing === id) {
      setEditing("");
      return;
    }
    setEditing(id);
    try {
      setForm(await api.call<Form>("clusters.get", { id }));
    } catch (e) {
      fail(e);
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      // Si manda tutto il modulo, che e' quello che l'operatore ha davanti.
      // La password della camera fa eccezione: `clusters.get` la restituisce
      // vuota, e rimandarla cosi' la cancellerebbe.
      const patch: Form = { ...form, id: editing };
      if (!patch["cameraPassword"]) delete patch["cameraPassword"];
      if (!patch["irCameraPassword"]) delete patch["irCameraPassword"];
      await api.call("clusters.update", patch);
      setEditing("");
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  const create = async () => {
    try {
      await api.call("clusters.create", addForm);
      setAddOpen(false);
      setAddForm({ thresholdL1: 100, thresholdL2: 500 });
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  const remove = async (id: string, name: string) => {
    // "Delete" e' letterale anche nel `.qml`: quella scritta non ha una
    // chiave, e inventarne una qui vorrebbe dire aggiungerla a nove lingue
    // per una parola che nell'app desktop resta comunque inglese.
    if (!window.confirm(`Delete — ${name}?`)) return;
    try {
      await api.call("clusters.remove", { id });
      if (editing === id) setEditing("");
      await reload();
    } catch (e) {
      fail(e);
    }
  };

  if (denied) {
    return (
      <div className="settings__pane">
        <p className="muted">{t("users.superAdminRequired")}</p>
      </div>
    );
  }

  const active = rows.filter((c) => (c as { isActive?: number }).isActive !== 0).length;

  return (
    <div className="settings__pane settings__pane--clusters">
      {error && <p className="alert">{error}</p>}

      <div className="ametrics">
        <div>
          <div className="ametric__label">{t("settings.activeClusters").toUpperCase()}</div>
          <div className="ametric__value">{active}</div>
        </div>
        <div>
          <div className="ametric__label">{t("settings.totalAlarms").toUpperCase()}</div>
          <div className="ametric__value ametric__value--warn">{kpis.totalAlarms ?? 0}</div>
        </div>
        <div>
          <div className="ametric__label">{t("settings.unacknowledged").toUpperCase()}</div>
          <div
            className={`ametric__value${(kpis.unacknowledged ?? 0) > 0 ? " ametric__value--crit" : ""}`}
          >
            {kpis.unacknowledged ?? 0}
          </div>
        </div>
      </div>

      <div className="admin__head">
        <h3 className="admin__title">{t("settings.clusterManagement").toUpperCase()}</h3>
        <span className="emissions__spacer" />
        <button className="btn--primary" onClick={() => setAddOpen(!addOpen)}>
          <Icon name={addOpen ? "x" : "plus"} size={14} />
          {addOpen ? t("settings.cancel").toUpperCase() : t("settings.addCluster").toUpperCase()}
        </button>
        <button className="iconbtn" title={t("settings.clusterManagement")} onClick={() => void reload()}>
          <Icon name="refresh-cw" size={16} />
        </button>
      </div>

      {addOpen && (
        <div className="ccard ccard--add">
          <h4>{t("settings.addCluster").toUpperCase()}</h4>
          <div className="cgrid">
            <Fld label={t("settings.name")} v={addForm["name"]} on={(x) => setAddForm({ ...addForm, name: x })} />
            <Fld label={t("settings.areaCode")} v={addForm["areaCode"]} on={(x) => setAddForm({ ...addForm, areaCode: x })} />
            <Fld label={t("settings.thresholdL1")} num v={addForm["thresholdL1"]} on={(x) => setAddForm({ ...addForm, thresholdL1: x })} />
            <Fld label={t("settings.thresholdL2")} num v={addForm["thresholdL2"]} on={(x) => setAddForm({ ...addForm, thresholdL2: x })} />
            <Fld label={t("fld.cameraIp")} v={addForm["cameraIp"]} on={(x) => setAddForm({ ...addForm, cameraIp: x })} />
            <Fld label={t("fld.sensorIp")} v={addForm["sensorIp"]} on={(x) => setAddForm({ ...addForm, sensorIp: x })} />
          </div>
          <button
            className="btn--primary ccard__create"
            disabled={!addForm["name"] || !addForm["areaCode"]}
            onClick={() => void create()}
          >
            {t("settings.create").toUpperCase()}
          </button>
        </div>
      )}

      {rows.map((c) => (
        <div className="ccard" key={c.id}>
          <div className="ccard__row">
            {/* Pallino di stato, 14 px: nel .qml pulsa su WARNING e CRITICAL —
                un allarme che non si muove non si distingue da un pannello
                fermo. */}
            <span className={`ccard__dot ccard__dot--${statusKey(c.status)}`} />
            <div className="ccard__id">
              <b className="ccard__name">{c.name || "—"}</b>
              <span className="ccard__area">{c.areaCode || "—"}</span>
            </div>
            <span className="emissions__spacer" />
            <div className="ccard__badges">
              <span className="cbadge cbadge--l1">L1 {c.thresholdL1 ?? 0}</span>
              <span className="cbadge cbadge--l2">L2 {c.thresholdL2 ?? 0}</span>
              {/* La lettura corrente accanto alle due soglie: separate non
                  dicono niente, e' il confronto che dice se l'impianto e'
                  sopra o sotto. Il colore e' quello dello stato. */}
              <span className={`cbadge cbadge--ppm cbadge--${statusKey(c.status)}`}>
                {(c.currentPpm ?? 0).toFixed(1)} ppm·m
              </span>
            </div>
            {/* La calibrazione ha senso solo dove c'e' una planimetria su cui
                segnare i quattro angoli. */}
            {c.plantImageUrl && onCalibrate && (
              <button className="ucard__act" title="Calibration" onClick={() => onCalibrate(c)}>
                <Icon name="maximize-2" size={18} />
              </button>
            )}
            <button className="ucard__act" title={t("settings.editCluster")} onClick={() => void open(c.id)}>
              <Icon name={editing === c.id ? "x" : "edit"} size={18} />
            </button>
            <button
              className="ucard__act ucard__act--danger"
              title="Delete"
              onClick={() => void remove(c.id, c.name)}
            >
              <Icon name="trash-2" size={18} />
            </button>
          </div>

          <div className="ccard__chips">
            {c.cameraIp && <span className="chip chip--unknown">CAM {c.cameraIp}</span>}
            {c.sensorIp && <span className="chip chip--unknown">SENS {c.sensorIp}</span>}
            {/* Dove passa il controllo PTZ. Non e' ridondante con CAM: su
                ONVIF coincidono, su Pelco-D via NPort no, e su seriale
                diretta non c'e' nessun IP. Senza questa riga, un rig che non
                si muove obbliga ad aprire il modulo per sapere dove stava
                andando il comando. */}
            <span className={`chip${ptzAccent(c) ? " chip--accent" : " chip--unknown"}`}>
              PTZ {ptzEndpoint(c)}
            </span>
            {c.moxaIp && <span className="chip chip--unknown">DCS {c.moxaIp}</span>}
            {Boolean(c.windEnabled) && (
              <span className="chip chip--unknown">WIND {c.windSensorIp || "—"}</span>
            )}
            {c.plantImageUrl && <span className="chip chip--online">{t("settings.plant").toUpperCase()}</span>}
            {c.activePtzTraceId && (
              <span className="chip chip--online">{t("settings.ptzTrace").toUpperCase()}</span>
            )}
            {Boolean(c.isOutOfService) && (
              <span className="chip chip--offline">{t("alarm.outOfService").toUpperCase()}</span>
            )}
            {!c.cameraIp && !c.sensorIp && !c.moxaIp && (
              <span className="muted small">{t("settings.noHardwareIp")}</span>
            )}
          </div>

          {editing === c.id && (
            <ClusterForm
              cluster={c}
              form={form}
              setF={setF}
              setForm={setForm}
              onSave={() => void save()}
              onCancel={() => setEditing("")}
              onReload={() => void reload()}
              onError={fail}
            />
          )}
        </div>
      ))}

      {rows.length === 0 && <p className="muted">{t("login.systemStatusNoClusters")}</p>}
    </div>
  );
}

function ClusterForm({
  cluster,
  form,
  setF,
  setForm,
  onSave,
  onCancel,
  onReload,
  onError,
}: {
  cluster: Cluster;
  form: Form;
  setF: (k: string, v: Val) => void;
  setForm: (f: (prev: Form) => Form) => void;
  onSave: () => void;
  onCancel: () => void;
  onReload: () => void;
  onError: (e: unknown) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();

  const model = String(form["ptzModel"] ?? "custom");
  const proto = String(form["cameraProtocol"] ?? "pelco-d");
  const transport = String(form["ptzTransportMode"] ?? "direct");
  const sensorMode = String(form["sensorTransportMode"] ?? "direct");
  const isIndus = model === "indusvision";
  const isBik = model === "bik-inform";

  return (
    <div className="cform">
      <Sec title={`${t("settings.name")} / ${t("settings.areaCode")}`} />
      <div className="cgrid">
        <Fld label={t("settings.name")} v={form["name"]} on={(x) => setF("name", x)} />
        <Fld label={t("settings.areaCode")} v={form["areaCode"]} on={(x) => setF("areaCode", x)} />
        <Fld label={t("settings.thresholdL1")} num v={form["thresholdL1"]} on={(x) => setF("thresholdL1", x)} />
        <Fld label={t("settings.thresholdL2")} num v={form["thresholdL2"]} on={(x) => setF("thresholdL2", x)} />
      </div>

      <Sec title="DCS (MOXA)" />
      <div className="cgrid">
        <Toggle label={t("fld.enableDcs")} v={form["moxaEnabled"] === true} on={(x) => setF("moxaEnabled", x)} />
        {form["moxaEnabled"] === true && (
          <Fld label={t("fld.moxaIp")} v={form["moxaIp"]} on={(x) => setF("moxaIp", x)} />
        )}
      </div>

      <Sec title={t("settings.ptzModel")} />
      <div className="cgrid">
        <Sel
          label={t("settings.ptzModelLabel")}
          options={PTZ_MODELS}
          v={model}
          on={(x) => setForm((f) => ({ ...f, ...presetFor(x, f) }))}
        />
      </div>
      <p className="muted small">{t("settings.ptzModelHint")}</p>

      <Sec title="CAMERA" />
      <div className="cgrid">
        {/* Il protocollo lo sceglie il modello, tranne su "custom": mostrarlo
            sempre inviterebbe a metterlo in disaccordo con il rig. */}
        {model === "custom" && (
          <Sel
            label={t("settings.cameraProtocol")}
            options={CAMERA_PROTOCOLS}
            v={proto}
            on={(x) => setF("cameraProtocol", x)}
          />
        )}
        <Fld label={t("fld.cameraIp")} v={form["cameraIp"]} on={(x) => setF("cameraIp", x)} />
        {(proto === "dahua-cgi" || proto === "onvif") && (
          <>
            <Fld label={t("login.username")} v={form["cameraUsername"]} on={(x) => setF("cameraUsername", x)} />
            <Fld label={t("login.password")} password v={form["cameraPassword"]} on={(x) => setF("cameraPassword", x)} />
          </>
        )}
        <Fld
          label="STREAM URL OVERRIDE (rtsp:// · http(s):// · file://)"
          wide
          v={form["rtspUrl"]}
          on={(x) => setF("rtspUrl", x)}
        />
      </div>

      <Sec title={t("hdr.irCamera")} />
      <div className="cgrid">
        <Fld label={t("fld.irCameraIp")} v={form["irCameraIp"]} on={(x) => setF("irCameraIp", x)} />
        <Fld label={t("fld.irCameraUsername")} v={form["irCameraUsername"]} on={(x) => setF("irCameraUsername", x)} />
        <Fld label={t("fld.irCameraPassword")} password v={form["irCameraPassword"]} on={(x) => setF("irCameraPassword", x)} />
        <Fld label={t("fld.irRtspUrl")} wide v={form["irRtspUrl"]} on={(x) => setF("irRtspUrl", x)} />
      </div>
      <p className="muted small">{t("settings.irCameraHint")}</p>

      {/* Su ONVIF lo zero e' una calibrazione, non un campo: si porta la testa
          dove deve stare e si dice "questa e' casa". */}
      {isIndus && (
        <>
          <Sec title="ZERO" />
          {/* Lo zero si imposta una volta e poi tutti i waypoint gli sono
              relativi: un errore qui li sposta tutti. Le due scritte sono
              letterali anche nel `.qml`. */}
          <p className="muted small">ZERO PTZ — porta il rig in home, poi premi</p>
          <button
            onClick={() => {
              void api
                .call("ptz.setOnvifZeroToCurrent", { clusterId: cluster.id })
                .then(onReload)
                .catch(onError);
            }}
          >
            SET PTZ ZERO TO CURRENT POSITION
          </button>
        </>
      )}

      {!isIndus && (
        <>
          <Sec title={t("hdr.ptzTransport")} />
          <div className="cgrid">
            {model === "custom" && (
              <Sel label="Mode" options={["direct", "nport"]} v={transport} on={(x) => setF("ptzTransportMode", x)} />
            )}
            {proto === "pelco-d" && (
              <Fld label={t("fld.homeDuration")} num v={form["ptzHomeDurationMs"] ?? 3000} on={(x) => setF("ptzHomeDurationMs", x)} />
            )}
            {(transport === "nport" || isBik) && (
              <>
                {/* Sul rig IP-nativo questo e' l'indirizzo del pan-tilt, non
                    del gateway seriale: sono due apparati diversi. */}
                <Fld
                  label={isBik ? t("fld.ptzIpOwn") : t("fld.ptzIpNport")}
                  v={form["ptzIp"]}
                  on={(x) => setF("ptzIp", x)}
                />
                <Fld label={t("fld.ptzPort")} num v={form["ptzPort"] ?? (isBik ? 61556 : 4001)} on={(x) => setF("ptzPort", x)} />
              </>
            )}
            {proto === "pelco-d" && (
              <Fld label={t("fld.deviceAddress")} num v={form["ptzDeviceAddress"] ?? 1} on={(x) => setF("ptzDeviceAddress", x)} />
            )}
          </div>
        </>
      )}

      <Sec title={t("hdr.sensorTransport")} />
      <div className="cgrid">
        <Sel label="Mode" options={SENSOR_MODES} v={sensorMode} on={(x) => setF("sensorTransportMode", x)} />
        <Fld label={t("fld.sensorIp")} v={form["sensorIp"]} on={(x) => setF("sensorIp", x)} />
        {sensorMode !== "direct" && (
          <>
            <Fld label={t("fld.sensorPort")} num v={form["sensorPort"] ?? 4001} on={(x) => setF("sensorPort", x)} />
            <Fld label={t("fld.pathLength")} num real v={form["sensorPathLengthM"] ?? 1} on={(x) => setF("sensorPathLengthM", x)} />
          </>
        )}
        <Fld label={t("fld.opticalBaseline")} num real v={form["opticalBaseline"] ?? 0} on={(x) => setF("opticalBaseline", x)} />
      </div>

      {/* Accensione del puntatore sul sensore Allred. I bottoni ci sono e
          sono SPENTI: nel C++ non e' una chiamata, e' un segnale interno dal
          bridge al worker che possiede l'unica connessione TCP al Moxa, e sul
          server quella rotta non esiste. Toglierli nasconderebbe la mancanza;
          lasciarli attivi darebbe un 404 in faccia a chi li preme. */}
      {sensorMode === "allred" && (
        <div className="ptz__row">
          <span className="muted small">{t("fld.laserPointer")}</span>
          <button disabled title="Nessuna rotta sul server: nel C++ e' un segnale interno al worker">
            {t("btn.laserOn")}
          </button>
          <button disabled title="Nessuna rotta sul server: nel C++ e' un segnale interno al worker">
            {t("btn.laserOff")}
          </button>
        </div>
      )}

      <Sec title="ANEMOMETER" />
      <div className="cgrid">
        <Toggle label={t("fld.enableWind")} v={form["windEnabled"] === true} on={(x) => setF("windEnabled", x)} />
      </div>
      {form["windEnabled"] === true && (
        <div className="cgrid">
          <Fld label={t("fld.windSensorIp")} v={form["windSensorIp"]} on={(x) => setF("windSensorIp", x)} />
          <Fld label={t("fld.windSensorPort")} num v={form["windSensorPort"] ?? 502} on={(x) => setF("windSensorPort", x)} />
          <Fld label={t("fld.sensorX")} num real v={form["sensorX"] ?? 0} on={(x) => setF("sensorX", x)} />
          <Fld label={t("fld.sensorY")} num real v={form["sensorY"] ?? 0} on={(x) => setF("sensorY", x)} />
          <Fld label={t("fld.laserEndX")} num real v={form["laserEndX"] ?? 0} on={(x) => setF("laserEndX", x)} />
          <Fld label={t("fld.laserEndY")} num real v={form["laserEndY"] ?? 0} on={(x) => setF("laserEndY", x)} />
        </div>
      )}

      <Sec title={t("hdr.laserBeamPointer")} />
      <div className="cgrid">
        <Toggle label={t("fld.enableLaser")} v={form["laserPointerEnabled"] === true} on={(x) => setF("laserPointerEnabled", x)} />
      </div>
      {form["laserPointerEnabled"] === true && (
        <div className="cgrid">
          <Sel label="Mode" options={["stable", "automatic"]} v={String(form["laserPointerMode"] ?? "stable")} on={(x) => setF("laserPointerMode", x)} />
          <Sel label="Frequency" options={["1", "2", "5"]} v={String(form["laserPointerHz"] ?? 1)} on={(x) => setF("laserPointerHz", Number(x))} />
          {String(form["laserPointerMode"] ?? "stable") === "stable" && (
            <>
              <Fld label={t("fld.hOffset")} num real v={form["laserPointerH"] ?? 0} on={(x) => setF("laserPointerH", x)} />
              <Fld label={t("fld.vOffset")} num real v={form["laserPointerV"] ?? 0} on={(x) => setF("laserPointerV", x)} />
            </>
          )}
        </div>
      )}
      {form["laserPointerEnabled"] === true &&
        String(form["laserPointerMode"] ?? "stable") === "automatic" && (
          <CalNodes value={String(form["laserPointerCal"] ?? "")} on={(v) => setF("laserPointerCal", v)} />
        )}

      <Sec title={t("hdr.laserMarkerVideo")} />
      <div className="cgrid">
        <Toggle label={t("fld.showOnVideo")} v={form["laserVideoMarkerEnabled"] === true} on={(x) => setF("laserVideoMarkerEnabled", x)} />
      </div>
      {form["laserVideoMarkerEnabled"] === true && (
        <div className="cgrid">
          <Fld label={t("fld.baseLaserCamera")} num real v={form["laserBaseB"] ?? 0.5} on={(x) => setF("laserBaseB", x)} />
          <Fld label={t("fld.verticalOffset")} num real v={form["laserVOff"] ?? 0} on={(x) => setF("laserVOff", x)} />
          <Fld label="FOV wide (°)" num real v={form["cameraFovWide"] ?? 55} on={(x) => setF("cameraFovWide", x)} />
          <Fld label="FOV tele (°)" num real v={form["cameraFovTele"] ?? 2.3} on={(x) => setF("cameraFovTele", x)} />
          <Sel
            label={t("fld.laserSide")}
            options={["left", "right"]}
            v={Number(form["laserSideSign"] ?? -1) < 0 ? "left" : "right"}
            on={(x) => setF("laserSideSign", x === "left" ? -1 : 1)}
          />
          <Fld label={t("fld.defaultDistance")} num real v={form["laserTargetDist"] ?? 25} on={(x) => setF("laserTargetDist", x)} />
          <Fld label={t("fld.boresightH")} num real v={form["laserBoresightH"] ?? 0} on={(x) => setF("laserBoresightH", x)} />
          <Fld label={t("fld.boresightV")} num real v={form["laserBoresightV"] ?? 0} on={(x) => setF("laserBoresightV", x)} />
        </div>
      )}
      {form["laserVideoMarkerEnabled"] === true && (
        <p className="muted small">{t("settings.laserMarkerHint")}</p>
      )}

      {/* Sul rig ONVIF i limiti li impone il firmware (tilt bloccato a ±45°):
          offrirli qui prometterebbe un controllo che non c'e'. */}
      {!isIndus && (
        <>
          <Sec title={t("settings.ptzLimits")} />
          <div className="cgrid">
            <Fld label="PAN Min (°)" num real v={form["ptzPanMin"] ?? -175} on={(x) => setF("ptzPanMin", x)} />
            <Fld label="PAN Max (°)" num real v={form["ptzPanMax"] ?? 175} on={(x) => setF("ptzPanMax", x)} />
            <Fld label="TILT Min (°)" num real v={form["ptzTiltMin"] ?? -90} on={(x) => setF("ptzTiltMin", x)} />
            <Fld label="TILT Max (°)" num real v={form["ptzTiltMax"] ?? 90} on={(x) => setF("ptzTiltMax", x)} />
          </div>
        </>
      )}

      <Sec title={t("settings.ptzAutoSpeed")} />
      <div className="cgrid">
        <Fld label={t("settings.ptzAutoSpeed")} num v={form["ptzAutoSpeed"] ?? 10} on={(x) => setF("ptzAutoSpeed", x)} />
        <Toggle
          label={t("settings.ptzPatrolAutoResume")}
          v={form["ptzPatrolAutoResume"] === true}
          on={(x) => setF("ptzPatrolAutoResume", x)}
        />
      </div>
      <p className="muted small">{t("settings.ptzAutoSpeedHint")}</p>
      <p className="muted small">{t("settings.ptzPatrolAutoResumeHint")}</p>

      <Sec title={t("hdr.deadbandDelay")} />
      <div className="cgrid">
        <Fld label={`${t("settings.deadband")} L1 (%)`} num real v={form["deadbandL1"] ?? 5} on={(x) => setF("deadbandL1", x)} />
        <Fld label={`${t("settings.deadband")} L2 (%)`} num real v={form["deadbandL2"] ?? 5} on={(x) => setF("deadbandL2", x)} />
        <Fld label={`${t("settings.onDelay")} (s)`} num v={form["onDelay"] ?? 3} on={(x) => setF("onDelay", x)} />
        <Fld label={`${t("settings.offDelay")} (s)`} num v={form["offDelay"] ?? 5} on={(x) => setF("offDelay", x)} />
      </div>

      <PlantSection cluster={cluster} form={form} setF={setF} onReload={onReload} onError={onError} />

      <OutOfServiceSection cluster={cluster} onReload={onReload} onError={onError} />

      <TraceSection cluster={cluster} onReload={onReload} onError={onError} />

      <div className="ucard__buttons">
        <button onClick={onCancel}>{t("settings.cancel")}</button>
        <button className="btn--primary" onClick={onSave}>
          {t("settings.save")}
        </button>
      </div>
    </div>
  );
}

/**
 * La planimetria e il percorso del fascio.
 *
 * I due punti si segnano cliccando SULL'IMMAGINE — primo clic l'emettitore,
 * secondo il riflettore — e non scrivendo quattro numeri fra zero e uno. Sono
 * le coordinate da cui nasce il fascio sulla vista IMPIANTO: sbagliarle di un
 * decimo vuol dire un raggio che punta a un altro pezzo dell'impianto.
 */
function PlantSection({
  cluster,
  form,
  setF,
  onReload,
  onError,
}: {
  cluster: Cluster;
  form: Form;
  setF: (k: string, v: Val) => void;
  onReload: () => void;
  onError: (e: unknown) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [next, setNext] = useState<"emitter" | "reflector">("emitter");

  const upload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      void api
        .call("clusters.plant_image", { clusterId: cluster.id, data: String(reader.result) })
        .then(onReload)
        .catch(onError);
    };
    reader.readAsDataURL(file);
  };

  const pick = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const r = img.getBoundingClientRect();
    // Il rettangolo DIPINTO, non quello del riquadro: con "contain" le bande
    // laterali sposterebbero ogni clic.
    const ar = img.naturalWidth / img.naturalHeight;
    const boxAr = r.width / r.height;
    const w = ar > boxAr ? r.width : r.height * ar;
    const h = ar > boxAr ? r.width / ar : r.height;
    const x = (e.clientX - r.left - (r.width - w) / 2) / w;
    const y = (e.clientY - r.top - (r.height - h) / 2) / h;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    const v = (n: number) => Math.round(n * 1000) / 1000;
    if (next === "emitter") {
      setF("sensorX", v(x));
      setF("sensorY", v(y));
      setNext("reflector");
    } else {
      setF("laserEndX", v(x));
      setF("laserEndY", v(y));
      setNext("emitter");
    }
  };

  const dot = (xk: string, yk: string, color: string) => {
    const x = Number(form[xk]);
    const y = Number(form[yk]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return (
      <span
        className="plantpick__dot"
        style={{ left: `${x * 100}%`, top: `${y * 100}%`, background: color }}
      />
    );
  };

  return (
    <>
      <Sec title={t("settings.clusterPlant")} />
      <div className="ptz__row">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload(f);
            e.target.value = "";
          }}
        />
        <button className="btn--primary" onClick={() => fileRef.current?.click()}>
          {cluster.plantImageUrl ? t("settings.replace") : t("settings.uploadPlant")}
        </button>
        {cluster.plantImageUrl && (
          <button
            className="logs__del"
            onClick={() => {
              void api
                .call("clusters.remove_plant_image", { clusterId: cluster.id })
                .then(onReload)
                .catch(onError);
            }}
          >
            {t("settings.remove")}
          </button>
        )}
        <span className="muted small">{t("msg.pngJpegMax")}</span>
        <span className={cluster.plantImageUrl ? "ok" : "muted"}>
          {cluster.plantImageUrl ? `✓ ${cluster.plantImageUrl.split("/").pop()}` : "—"}
        </span>
      </div>

      {cluster.plantImageUrl && (
        <div className="plantpick">
          <div className="muted small">
            LASER BEAM PATH — {next === "emitter" ? "1° click = emettitore" : "2° click = riflettore"}
          </div>
          <div className="plantpick__box">
            <img src={cluster.plantImageUrl} alt="" onClick={pick} />
            {dot("sensorX", "sensorY", "#22c55e")}
            {dot("laserEndX", "laserEndY", "#ef4444")}
          </div>
        </div>
      )}
    </>
  );
}

function OutOfServiceSection({
  cluster,
  onReload,
  onError,
}: {
  cluster: Cluster;
  onReload: () => void;
  onError: (e: unknown) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();
  const [reason, setReason] = useState("");

  const toggle = () => {
    void api
      .call("clusters.out_of_service", {
        id: cluster.id,
        isOutOfService: !cluster.isOutOfService,
        reason,
      })
      .then(onReload)
      .catch(onError);
  };

  return (
    <>
      <Sec title={t("alarm.outOfService")} />
      <div className="ptz__row">
        <button className={cluster.isOutOfService ? "logs__del" : undefined} onClick={toggle}>
          {cluster.isOutOfService ? t("settings.deactivate") : t("alarm.outOfService")}
        </button>
        {!cluster.isOutOfService && (
          <input
            placeholder={t("alarm.oosReason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </div>
      {Boolean(cluster.isOutOfService) && (
        <p className="muted small">
          {t("alarm.oosBy")}: {cluster.outOfServiceBy || "—"} · {t("alarm.oosReason")}:{" "}
          {cluster.outOfServiceReason || "—"}
        </p>
      )}
    </>
  );
}

function TraceSection({
  cluster,
  onReload,
  onError,
}: {
  cluster: Cluster;
  onReload: () => void;
  onError: (e: unknown) => void;
}) {
  const { t } = useI18n();
  const { api, can } = useSession();
  const [traces, setTraces] = useState<{ id: string; name: string; waypoints: unknown[] }[]>([]);
  const [activeId, setActiveId] = useState(cluster.activePtzTraceId ?? "");

  const load = useCallback(() => {
    void api
      .call<{ id: string; name: string; waypoints: unknown[] }[]>("ptz-traces.list", {
        clusterId: cluster.id,
      })
      .then(setTraces)
      .catch(() => {
        /* nessuna traccia leggibile: la sezione resta vuota */
      });
  }, [api, cluster.id]);

  useEffect(load, [load]);

  const select = (id: string) => {
    const next = activeId === id ? null : id;
    void api
      .call("clusters.update", { id: cluster.id, activePtzTraceId: next })
      .then(() => {
        setActiveId(next ?? "");
        onReload();
      })
      .catch(onError);
  };

  return (
    <>
      <Sec title={t("settings.ptzTraces")} />
      {traces.length === 0 && <p className="muted small">{t("settings.noTraceRecorded")}</p>}
      <ul className="tracelist">
        {traces.map((tr) => (
          <li key={tr.id}>
            <button className={`link${activeId === tr.id ? " on" : ""}`} onClick={() => select(tr.id)}>
              <Icon name={activeId === tr.id ? "check-circle" : "play"} size={12} />
              {tr.name}
              <span className="muted small">
                {Array.isArray(tr.waypoints) ? tr.waypoints.length : 0} {t("ptz.points")}
              </span>
            </button>
            {can("deleteTraces") && (
              <button
                className="ucard__act ucard__act--danger"
                title={t("settings.remove")}
                onClick={() => {
                  void api
                    .call("ptz-traces.remove", { id: tr.id })
                    .then(() => {
                      load();
                      onReload();
                    })
                    .catch(onError);
                }}
              >
                <Icon name="trash-2" size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * I nodi di calibrazione del puntatore, in modo automatico.
 *
 * Ogni riga dice: a QUESTO zoom, lo scostamento del puntatore e' questo. Fra
 * un nodo e l'altro il valore si interpola. Con un nodo solo non c'e' niente
 * da interpolare e lo scostamento resta quello — che e' esattamente il modo
 * "fisso", ed e' il motivo per cui la tabella parte da due.
 *
 * Vive in un campo TESTO del cluster (`laserPointerCal`) come JSON: e' la
 * stessa colonna che legge l'app desktop, e cambiarne la forma vorrebbe dire
 * che una calibrazione fatta di qua non si legge di la'.
 */
function CalNodes({ value, on }: { value: string; on: (v: string) => void }) {
  const { t } = useI18n();
  let nodes: { zoom: number; h: number; v: number }[] = [];
  try {
    const parsed: unknown = JSON.parse(value || "[]");
    if (Array.isArray(parsed)) {
      nodes = parsed.filter(
        (n): n is { zoom: number; h: number; v: number } =>
          !!n && typeof n === "object" && typeof (n as { zoom?: unknown }).zoom === "number",
      );
    }
  } catch {
    // Colonna scritta male: si riparte da vuoto invece di far saltare la
    // scheda. La calibrazione si rifa', la configurazione no.
    nodes = [];
  }

  const write = (next: typeof nodes) => on(JSON.stringify(next));
  const setAt = (i: number, key: "zoom" | "h" | "v", v: number) =>
    write(nodes.map((n, k) => (k === i ? { ...n, [key]: v } : n)));

  return (
    <div className="calnodes">
      <div className="calnodes__row calnodes__row--head">
        <span>ZOOM</span>
        <span>H</span>
        <span>V</span>
        <span />
      </div>
      {nodes.map((n, i) => (
        <div className="calnodes__row" key={i}>
          <input type="number" step="any" value={n.zoom} onChange={(e) => setAt(i, "zoom", Number(e.target.value))} />
          <input type="number" step="any" value={n.h} onChange={(e) => setAt(i, "h", Number(e.target.value))} />
          <input type="number" step="any" value={n.v} onChange={(e) => setAt(i, "v", Number(e.target.value))} />
          <button
            className="ucard__act ucard__act--danger"
            onClick={() => write(nodes.filter((_, k) => k !== i))}
          >
            <Icon name="x" size={12} />
          </button>
        </div>
      ))}
      <button className="calnodes__add" onClick={() => write([...nodes, { zoom: 0, h: 0, v: 0 }])}>
        <Icon name="plus" size={12} />
        {t("settings.addCalNode")}
      </button>
      <p className="muted small">{t("settings.calInterpHint")}</p>
    </div>
  );
}

// ── mattoncini del modulo ───────────────────────────────────────────────────

function Sec({ title }: { title: string }) {
  return <h4 className="csec">{title}</h4>;
}

function Fld({
  label,
  v,
  on,
  num,
  real,
  password,
  wide,
}: {
  label: string;
  v: Val;
  on: (v: Val) => void;
  num?: boolean;
  real?: boolean;
  password?: boolean;
  wide?: boolean;
}) {
  return (
    <label className={wide ? "cfield cfield--wide" : "cfield"}>
      <span>{label}</span>
      <input
        type={password ? "password" : num ? "number" : "text"}
        step={real ? "any" : undefined}
        value={v === null || v === undefined ? "" : String(v)}
        onChange={(e) => {
          const raw = e.target.value;
          on(num ? (raw === "" ? null : Number(raw)) : raw);
        }}
      />
    </label>
  );
}

function Sel({
  label,
  options,
  v,
  on,
}: {
  label: string;
  options: string[];
  v: string;
  on: (v: string) => void;
}) {
  return (
    <label className="cfield">
      <span>{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function Toggle({ label, v, on }: { label: string; v: boolean; on: (v: boolean) => void }) {
  return (
    <span className="cfield cfield--toggle">
      <Switch label={label} on={v} onChange={on} />
    </span>
  );
}
